/**
 * SeqAI access policy:
 * - Local / development frontend → always allowed
 * - Production frontend → allowed only if the configured SeqAI API URL is reachable
 *   (office LAN / Tailscale / localhost tunnel). Otherwise the UI is blocked.
 */

export const SEQAI_API_BASE_URL =
  process.env.REACT_APP_SEQAI_API_BASE_URL || 'http://localhost:8008';

export function isSeqaiDevEnvironment(): boolean {
  const env = (process.env.REACT_APP_ENVIRONMENT || 'development').toLowerCase();
  if (env === 'development' || env === 'local' || env === 'dev') {
    return true;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      return true;
    }
  }
  return false;
}

export type SeqaiAvailability =
  | { status: 'checking' }
  | { status: 'allowed'; reason: 'local' | 'reachable' }
  | { status: 'blocked'; reason: 'unreachable'; apiUrl: string };

export async function probeSeqaiAvailability(timeoutMs = 3500): Promise<SeqaiAvailability> {
  if (isSeqaiDevEnvironment()) {
    return { status: 'allowed', reason: 'local' };
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${SEQAI_API_BASE_URL.replace(/\/$/, '')}/health`, {
      method: 'GET',
      signal: controller.signal,
      // Health is public; no credentials needed for the gate check.
      mode: 'cors',
    });
    if (res.ok) {
      return { status: 'allowed', reason: 'reachable' };
    }
    return { status: 'blocked', reason: 'unreachable', apiUrl: SEQAI_API_BASE_URL };
  } catch {
    return { status: 'blocked', reason: 'unreachable', apiUrl: SEQAI_API_BASE_URL };
  } finally {
    window.clearTimeout(timer);
  }
}
